import { useMemo } from 'react'
import { defineMessage, useIntl } from 'react-intl'
import { EmptyArray } from 'lib/array/arrayUtils'
import { isHotelPackage } from 'lib/offer/offerTypes'
import { dedupeImages, formatRoomLabel, getImageKey, groupImagesByTitle } from '../offerGalleryImageUtils'
import { ALL_PHOTOS_CATEGORY_ID } from './GalleryModalV2.types'
import type { GalleryCategory } from './GalleryModalV2.types'

const MIN_CATEGORIES_FOR_TABS = 3
const MIN_IMAGES_PER_SPACE_CATEGORY = 3

const propertyHighlightsMessage = defineMessage({
  id: 'wvMEic64NpgrAUyYp6y1',
  defaultMessage: 'Property',
  description: 'Label for the property-level photos category tab and section in the categorised offer page gallery, shown alongside individual room categories',
})

function buildRoomTypeCategories(
  offer: App.AnyOffer | undefined,
  rooms: Array<App.Property.Room>,
): Array<GalleryCategory> {
  if (!offer) return EmptyArray

  const categoriesByName = new Map<string, GalleryCategory>()

  const addRoom = (id?: string, name?: string, images?: Array<App.Image>) => {
    if (!id || !name || !images?.length) return

    const label = formatRoomLabel(name)
    const nameKey = label.toLowerCase()
    const existing = categoriesByName.get(nameKey)

    categoriesByName.set(nameKey, {
      id: existing?.id ?? id,
      label: existing?.label ?? label,
      images: dedupeImages([...(existing?.images ?? []), ...images]),
      type: 'room',
    })
  }

  rooms.forEach((room) => {
    addRoom(room.id, room.name, room.images)
  })

  if ('packages' in offer && offer.packages) {
    offer.packages.forEach((pkg) => {
      if ('roomId' in pkg && 'images' in pkg) {
        addRoom(pkg.roomId, pkg.name, pkg.images)
        return
      }
      if (isHotelPackage(pkg)) {
        addRoom(pkg.roomType.id, pkg.roomType.name, pkg.roomType.images)
      }
    })
  }

  return Array.from(categoriesByName.values())
}

function buildSpaceCategories(images: Array<App.Image>): Array<GalleryCategory> {
  const groupedByTitle = groupImagesByTitle(images)

  return Array.from(groupedByTitle.entries()).map(([title, groupImages]) => ({
    id: title,
    label: title,
    images: groupImages,
    type: 'space',
  }))
}

function partitionMysteryRoomImages(
  images: Array<App.Image>,
  propertyRooms: Array<App.Property.Room>,
): { nonMysteryRooms: Array<App.Property.Room>, nonMysteryImages: Array<App.Image> } {
  const nonMysteryRooms = propertyRooms.filter(room => !room.isMysteryRoom)
  const mysteryRoomImageKeys = new Set(
    propertyRooms
      .filter(room => room.isMysteryRoom)
      .flatMap(room => room.images.map((image, index) => getImageKey(image, index))),
  )
  const nonMysteryImages = images.filter((image, index) => !mysteryRoomImageKeys.has(getImageKey(image, index)))

  return { nonMysteryRooms, nonMysteryImages }
}

function useGalleryV2Categories(
  offer: App.AnyOffer | undefined,
  images: Array<App.Image>,
  propertyRooms: Array<App.Property.Room> = EmptyArray,
): Array<GalleryCategory> {
  const intl = useIntl()

  return useMemo(() => {
    const { nonMysteryRooms, nonMysteryImages } = partitionMysteryRoomImages(images, propertyRooms)

    const roomTypeCategories = buildRoomTypeCategories(offer, nonMysteryRooms)
    const dedupedImages = dedupeImages(nonMysteryImages)

    const isRoomTabsMode = roomTypeCategories.length >= MIN_CATEGORIES_FOR_TABS
    const allSpaceCategories = isRoomTabsMode ? EmptyArray : buildSpaceCategories(dedupedImages)
    const spaceCategories = allSpaceCategories.filter(category => category.images.length >= MIN_IMAGES_PER_SPACE_CATEGORY)
    const leftoverSpaceImages = dedupeImages(
      allSpaceCategories
        .filter(category => category.images.length < MIN_IMAGES_PER_SPACE_CATEGORY)
        .flatMap(category => category.images),
    )
    const hasEnoughSpaceCategories = spaceCategories.length >= MIN_CATEGORIES_FOR_TABS

    const sectionCategories = isRoomTabsMode
      ? roomTypeCategories
      : (hasEnoughSpaceCategories ? spaceCategories : EmptyArray)

    if (sectionCategories.length < MIN_CATEGORIES_FOR_TABS) {
      return EmptyArray as Array<GalleryCategory>
    }

    const roomImageKeys = new Set(
      roomTypeCategories.flatMap(category => category.images.map((image, index) => getImageKey(image, index))),
    )
    const propertyHighlightImages = isRoomTabsMode
      ? dedupedImages.filter((image, index) => !roomImageKeys.has(getImageKey(image, index)))
      : leftoverSpaceImages

    const propertyHighlights: Array<GalleryCategory> = propertyHighlightImages.length
      ? [{
          id: 'property-highlights',
          label: intl.formatMessage(propertyHighlightsMessage),
          images: propertyHighlightImages,
        }]
      : EmptyArray

    const allPhotosImages = dedupeImages([
      ...propertyHighlights,
      ...sectionCategories,
    ].flatMap(category => category.images))

    return [
      { id: ALL_PHOTOS_CATEGORY_ID, label: '', images: allPhotosImages },
      ...propertyHighlights,
      ...sectionCategories,
    ]
  }, [images, intl, offer, propertyRooms])
}

export default useGalleryV2Categories
